import { isAfter } from 'date-fns'
import * as Yup from 'yup'

import { FORMIK_EMPTY_VALUE } from '../../../../../../constants/validation'
import {
  APPLICANT_CREDIT_CHECK_BAND_LABEL_KEY,
  getApplicantFieldValue,
} from '../../../../../RentalPortalApplicant'
import {
  getListingDateValue,
  parseListingFormDate,
} from '../../../../RentalPortal.utils'

import {
  APPLICANT_CREDIT_CHECK_VERDICT_BAND_ORDER,
  APPLICANT_CREDIT_CHECK_VERDICT_CODE_MAX_LENGTH,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE,
  APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED,
  APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE_MAX_LENGTH,
  APPLICANT_CREDIT_CHECK_VERDICT_ROLE_VALUE_KEY,
} from './ApplicantCreditCheckVerdictModal.constants'

export function getApplicantCreditCheckVerdictInitialValues({
  extraction,
  result,
}) {
  return {
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND]:
      extraction?.normalised_band ??
      getApplicantFieldValue(result?.normalised_band) ??
      FORMIK_EMPTY_VALUE,
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE]:
      result?.verification_code ?? FORMIK_EMPTY_VALUE,
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT]:
      parseListingFormDate(result?.expires_at),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]: parseListingFormDate(
      extraction?.issued_at ?? result?.issued_at
    ),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE]:
      extraction?.native_value ??
      getApplicantFieldValue(result?.native_value) ??
      FORMIK_EMPTY_VALUE,
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED]: Boolean(
      result?.verified_at
    ),
  }
}

export function getApplicantCreditCheckVerdictExtractedFields({
  extraction,
  values,
}) {
  return {
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND]: Boolean(
      extraction?.normalised_band &&
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND] ===
        extraction.normalised_band
    ),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]: Boolean(
      extraction?.issued_at &&
      getListingDateValue(
        values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]
      ) === extraction.issued_at
    ),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE]: Boolean(
      extraction?.native_value &&
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE] ===
        extraction.native_value
    ),
  }
}

export function getApplicantCreditCheckVerdictPayload(values) {
  return {
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND]:
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND],
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE]:
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE].trim() || null,
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT]: getListingDateValue(
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT]
    ),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]: getListingDateValue(
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]
    ),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE]:
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE].trim() ||
      null,
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED]:
      values[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED],
  }
}

export function getApplicantCreditCheckVerdictValidationSchema(t) {
  return Yup.object({
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_BAND]: Yup.string()
      .nullable()
      .required(true),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_CODE]: Yup.string()
      .nullable()
      .max(APPLICANT_CREDIT_CHECK_VERDICT_CODE_MAX_LENGTH, true)
      .when(APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED, {
        is: true,
        then: schema => schema.required(true),
      }),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_EXPIRES_AT]: Yup.date()
      .nullable()
      .test({
        message: t('RentalPortal.ApplicantCreditCheckVerdictExpiresAtErrorKey'),
        name: 'applicantCreditCheckVerdictExpiresAfterIssued',
        test: function (value) {
          return (
            !value ||
            !this.parent[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT] ||
            isAfter(
              value,
              this.parent[APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]
            )
          )
        },
      }),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_ISSUED_AT]: Yup.date()
      .nullable()
      .required(true)
      .test({
        message: t(
          'RentalPortal.ApplicantCreditCheckVerdictIssuedAtFutureErrorKey'
        ),
        name: 'applicantCreditCheckVerdictIssuedNotInFuture',
        test: value => !value || !isAfter(value, new Date()),
      }),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_NATIVE_VALUE]: Yup.string()
      .nullable()
      .max(APPLICANT_CREDIT_CHECK_VERDICT_NATIVE_VALUE_MAX_LENGTH, true),
    [APPLICANT_CREDIT_CHECK_VERDICT_FIELD_NAME_VERIFIED]:
      Yup.boolean().notRequired(),
  })
}

export function getApplicantCreditCheckVerdictBandOptions(t) {
  return APPLICANT_CREDIT_CHECK_VERDICT_BAND_ORDER.map(band => ({
    id: band,
    label: t(APPLICANT_CREDIT_CHECK_BAND_LABEL_KEY[band]),
  }))
}

export function getApplicantCreditCheckVerdictRoleText({ result, t }) {
  return result?.role
    ? t(APPLICANT_CREDIT_CHECK_VERDICT_ROLE_VALUE_KEY[result.role])
    : null
}
